import { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import api from '@/lib/api';
import { thumbUrl } from '@/lib/imageUrl';
import { formatEventCost } from '@/lib/formatEventCost';
import { formatEventTime } from '@/lib/formatEventTime';
import { formatEventDate, relativeEventLabel } from '@/lib/formatEventDate';
import { Calendar, MapPin, Users, Plus, Search, Loader2, GraduationCap, Trophy, Sparkles, Gamepad2, Heart, LayoutGrid, List } from 'lucide-react';
import { Input } from '@/components/ui/input';
import SEO from '@/components/SEO';
import { PremiumInstructorBadge } from '@/components/PremiumInstructorBadge';
import { CommunityGrowthNote } from '@/components/CommunityGrowthNote';

const TYPE_META = {
  teaching: { label: 'Teaching Session', icon: GraduationCap, color: '#56c6df' },
  tournament: { label: 'Tournament', icon: Trophy, color: '#df5681' },
  casual: { label: 'Open Play', icon: Gamepad2, color: '#56dfb4' },
  special: { label: 'Special Event', icon: Sparkles, color: '#b456df' },
};

const SKILL_OPTIONS = ['newbie', 'beginner', 'intermediate', 'advanced'];
const DISTANCE_OPTIONS = [{ value: '10', label: '10 mi' }, { value: '25', label: '25 mi' }, { value: '50', label: '50 mi' }, { value: '100', label: '100 mi' }, { value: 'any', label: 'Any' }];
const SORT_OPTIONS = [
  { value: 'nearest', label: 'Nearest First' },
  { value: 'soonest', label: 'Soonest First' },
  { value: 'latest', label: 'Latest First' },
];

export default function EventsPage() {
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(false);
  // Default distance filter is 100 mi (not "any") so when a user types a
  // zip code they get LOCAL results, not nationwide-sorted-by-distance.
  // Caught 24 Jun 2026 — a user typed a Minnesota zip and got FL + SC
  // events back because "any" matched everywhere and there were no MN
  // events that day. Users can still widen to "Any" with one click.
  const [filters, setFilters] = useState({ event_type: [], is_free: '', skill_levels: [], max_distance: '100', is_destination_event: false });
  // Default sort is "soonest" (chronological, earliest first). Users
  // can swap to "nearest" or "latest" via the sort dropdown — their
  // pick persists for the session via the existing state.
  const [sortBy, setSortBy] = useState('soonest');
  const [zipCode, setZipCode] = useState('');
  // View mode: 'grid' (default tile view) or 'list' (compact row view).
  // Persisted to localStorage so returning visitors see their last choice.
  const [viewMode, setViewMode] = useState(() => {
    try {
      const v = localStorage.getItem('mfm_events_view_mode');
      return v === 'list' ? 'list' : 'grid';
    } catch {
      return 'grid';
    }
  });
  useEffect(() => {
    try { localStorage.setItem('mfm_events_view_mode', viewMode); } catch { /* ignore */ }
  }, [viewMode]);

  // Default zip to user's profile zip code
  useEffect(() => {
    if (user?.zip_code && !zipCode) setZipCode(user.zip_code);
  }, [user, zipCode]);

  const filtersRef = useRef(filters);
  filtersRef.current = filters;
  const zipRef = useRef(zipCode);
  zipRef.current = zipCode;

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    try {
      const f = filtersRef.current;
      const params = new URLSearchParams();
      if (zipRef.current) params.set('zip_code', zipRef.current);
      if (f.event_type.length) params.set('event_type', f.event_type.join(','));
      if (f.is_free === 'true') params.set('is_free', 'true');
      if (f.is_free === 'false') params.set('is_free', 'false');
      if (f.skill_levels.length) params.set('skill_levels', f.skill_levels.join(','));
      if (f.max_distance !== 'any') params.set('max_distance', f.max_distance);
      if (f.is_destination_event) params.set('is_destination_event', 'true');
      const { data } = await api.get(`/events?${params.toString()}`);
      setEvents(data.events || []);
      setPreview(data.preview || false);
    } catch {
      setEvents([]);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchEvents(); }, [fetchEvents, user, filters, zipCode]);

  const toggleFilter = (key, value) => {
    setFilters(p => ({ ...p, [key]: p[key].includes(value) ? p[key].filter(v => v !== value) : [...p[key], value] }));
  };

  // 'nearest' uses the server's (distance ASC → paid-host tier → date)
  // ordering as-is. 'soonest'/'latest' override that with a date-only sort
  // on the client so the user can opt out of distance when they prefer.
  const sortedEvents = sortBy === 'nearest'
    ? events
    : [...events].sort((a, b) => {
        const dateA = a.event_date || '';
        const dateB = b.event_date || '';
        return sortBy === 'latest' ? dateB.localeCompare(dateA) : dateA.localeCompare(dateB);
      });

  return (
    <div data-testid="events-page" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <SEO
        title="Mahjong Events, Tournaments &amp; Meetups Near You"
        description="Browse upcoming American Mahjong events, tournaments, teaching sessions, and casual meetups. Filter by city or distance and register securely online."
        path="/events"
      />
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>Events</h1>
          <p className="text-slate-500 mt-1">Discover Mahjong events near you</p>
        </div>
        {user && (
          <Link to="/events/post">
            <Button className="rounded-full bg-[#df5681] text-white hover:bg-[#c44570] gap-2" data-testid="post-event-btn">
              <Plus className="w-4 h-4" /> Post an Event
            </Button>
          </Link>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Filters */}
        <Card className="lg:col-span-1 border-slate-100 h-fit">
          <CardContent className="p-5 space-y-5">
            <div>
              <Label className="text-sm font-medium mb-1.5 block">Search by Zip Code</Label>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  value={zipCode}
                  onChange={e => setZipCode(e.target.value.replace(/\D/g, '').slice(0, 5))}
                  placeholder="Enter zip code"
                  className="pl-10 rounded-xl text-sm"
                  maxLength={5}
                  data-testid="events-zip-search"
                />
              </div>
              {user?.zip_code && zipCode !== user.zip_code && (
                <button onClick={() => setZipCode(user.zip_code)} className="text-xs text-[#56c6df] hover:underline mt-1">Reset to my zip ({user.zip_code})</button>
              )}
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-sm font-medium">Event Type</Label>
                <button
                  type="button"
                  onClick={() => setFilters(p => ({ ...p, event_type: [] }))}
                  className={`px-2 py-0.5 rounded-full text-[11px] border transition-all ${filters.event_type.length === 0 ? 'bg-[#56dfb4] text-slate-900 border-[#56dfb4]' : 'border-slate-200 text-slate-500 hover:bg-slate-50'}`}
                  data-testid="filter-event-type-any"
                >
                  Any
                </button>
              </div>
              <div className="space-y-1.5">
                {Object.entries(TYPE_META).map(([key, meta]) => (
                  <label key={key} className="flex items-center gap-2 cursor-pointer">
                    <Checkbox checked={filters.event_type.includes(key)} onCheckedChange={() => toggleFilter('event_type', key)} />
                    <meta.icon className="w-3.5 h-3.5" style={{ color: meta.color }} />
                    <span className="text-sm">{meta.label}</span>
                  </label>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-sm font-medium mb-2 block">Cost</Label>
              <div className="flex flex-wrap gap-2">
                {[{ v: '', l: 'All' }, { v: 'true', l: 'Free' }, { v: 'false', l: 'Paid' }].map(o => (
                  <button key={o.v} onClick={() => setFilters(p => ({ ...p, is_free: o.v }))}
                    className={`px-3 py-1.5 rounded-full text-xs border transition-all ${filters.is_free === o.v ? 'bg-[#56dfb4] text-slate-900 border-[#56dfb4]' : 'border-slate-200'}`}>
                    {o.l}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="flex items-start gap-2.5 cursor-pointer group" data-testid="filter-destination-toggle-label">
                <Checkbox
                  checked={filters.is_destination_event}
                  onCheckedChange={(v) => setFilters(p => ({ ...p, is_destination_event: !!v }))}
                  data-testid="filter-destination-toggle"
                  className="mt-0.5"
                />
                <span className="flex-1">
                  <span className="text-sm font-medium flex items-center gap-1.5">
                    <span role="img" aria-label="globe">🌍</span> Destination Events Only
                  </span>
                  <span className="block text-xs text-slate-500 mt-0.5 leading-snug">
                    Cruises, retreats &amp; fly-in events open to players nationwide
                  </span>
                </span>
              </label>
            </div>
            <div>
              <Label className="text-sm font-medium mb-2 block">Distance</Label>
              <div className="flex flex-wrap gap-2">
                {DISTANCE_OPTIONS.map(o => (
                  <button key={o.value} onClick={() => setFilters(p => ({ ...p, max_distance: o.value }))}
                    className={`px-3 py-1.5 rounded-full text-xs border transition-all ${filters.max_distance === o.value ? 'bg-[#56c6df] text-white border-[#56c6df]' : 'border-slate-200'}`}>
                    {o.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-sm font-medium">Skill Level</Label>
                <button
                  type="button"
                  onClick={() => setFilters(p => ({ ...p, skill_levels: [] }))}
                  className={`px-2 py-0.5 rounded-full text-[11px] border transition-all ${filters.skill_levels.length === 0 ? 'bg-[#56dfb4] text-slate-900 border-[#56dfb4]' : 'border-slate-200 text-slate-500 hover:bg-slate-50'}`}
                  data-testid="filter-skill-any"
                >
                  Any
                </button>
              </div>
              <div className="space-y-1.5">
                {SKILL_OPTIONS.map(s => (
                  <label key={s} className="flex items-center gap-2 cursor-pointer">
                    <Checkbox checked={filters.skill_levels.includes(s)} onCheckedChange={() => toggleFilter('skill_levels', s)} />
                    <span className="text-sm capitalize">{s}</span>
                  </label>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Events Grid */}
        <div className="lg:col-span-3">
          {loading ? (
            <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#56dfb4]" /></div>
          ) : events.length === 0 ? (
            <div className="text-center py-16 px-6" data-testid="events-empty-state">
              <Calendar className="w-12 h-12 mx-auto mb-4 text-[#56dfb4] opacity-70" />
              <p className="text-lg font-semibold text-slate-700" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {zipCode ? `No events near ${zipCode} yet` : 'No events match these filters yet'}
              </p>
              <p className="text-sm text-slate-500 mt-2 max-w-md mx-auto leading-relaxed">
                Mahjong communities grow when someone starts. <span className="font-medium text-slate-700">Posting a free-to-attend event costs nothing</span> for any tier — your living room, the local library, or a coffee shop. Be the first.
              </p>
              <div className="flex flex-wrap items-center justify-center gap-3 mt-5">
                <Link to={user ? '/events/post' : '/register?next=/events/post'}>
                  <Button className="rounded-full text-white font-semibold border-0 hover:brightness-110 transition-all" style={{ backgroundImage: 'linear-gradient(135deg, #56dfb4, #56c6df, #b456df)' }} data-testid="events-empty-post-cta">
                    {user ? 'Post an event' : 'Sign up free and post'}
                  </Button>
                </Link>
                {zipCode && filters.max_distance !== 'any' && (
                  <button
                    onClick={() => setFilters(p => ({ ...p, max_distance: 'any' }))}
                    className="text-sm text-slate-500 hover:text-slate-700 underline"
                    data-testid="events-empty-widen-distance"
                  >
                    Or widen to <strong>Any distance</strong>
                  </button>
                )}
                {zipCode && (
                  <button onClick={() => setZipCode('')} className="text-sm text-slate-500 hover:text-slate-700 underline" data-testid="events-empty-clear-zip">
                    Or clear the zip filter
                  </button>
                )}
              </div>
            </div>
          ) : (
            <>
              <CommunityGrowthNote surface="events" />
              {!user && (
                <div className="mb-6 p-4 rounded-2xl bg-[#56dfb4]/5 border border-[#56dfb4]/20" data-testid="events-anon-cta">
                  <p className="text-sm font-semibold text-slate-700 mb-2">Sign up free to get more from events:</p>
                  <ul className="text-sm text-slate-600 space-y-1 list-disc list-inside ml-1">
                    <li>Save events to your favorites and find them later</li>
                    <li>Mark yourself "interested" so hosts know to expect you</li>
                    <li>Get email or SMS alerts when new events post near your zip</li>
                    <li>Message hosts directly</li>
                  </ul>
                  <Link to="/register" className="inline-block mt-3 text-sm font-semibold bg-clip-text text-transparent hover:underline" style={{ backgroundImage: 'linear-gradient(135deg, #56dfb4, #56c6df, #b456df)' }}>
                    Sign up free &rarr;
                  </Link>
                </div>
              )}
              <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
                <p className="text-sm text-slate-500">
                  <strong className="text-slate-700">{sortedEvents.length}</strong> event{sortedEvents.length === 1 ? '' : 's'} found
                </p>
                <div className="flex items-center gap-3 flex-wrap">
                  <div
                    className="inline-flex items-center rounded-full border border-slate-200 bg-white p-0.5"
                    role="group"
                    aria-label="View mode"
                    data-testid="events-view-toggle"
                  >
                    <button
                      type="button"
                      onClick={() => setViewMode('grid')}
                      aria-pressed={viewMode === 'grid'}
                      aria-label="Tile view"
                      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs transition-colors ${
                        viewMode === 'grid'
                          ? 'bg-[#56dfb4] text-slate-900'
                          : 'text-slate-500 hover:text-slate-700'
                      }`}
                      data-testid="events-view-grid-btn"
                    >
                      <LayoutGrid className="w-3.5 h-3.5" />
                      Tiles
                    </button>
                    <button
                      type="button"
                      onClick={() => setViewMode('list')}
                      aria-pressed={viewMode === 'list'}
                      aria-label="List view"
                      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs transition-colors ${
                        viewMode === 'list'
                          ? 'bg-[#56dfb4] text-slate-900'
                          : 'text-slate-500 hover:text-slate-700'
                      }`}
                      data-testid="events-view-list-btn"
                    >
                      <List className="w-3.5 h-3.5" />
                      List
                    </button>
                  </div>
                  <div className="flex items-center gap-2">
                    <Label className="text-xs text-slate-500">Sort by:</Label>
                    <select
                      value={sortBy}
                      onChange={e => setSortBy(e.target.value)}
                      className="text-xs border border-slate-200 rounded-full px-3 py-1.5 bg-white focus:outline-none focus:ring-1 focus:ring-[#56dfb4]"
                      data-testid="events-sort-select"
                    >
                      {SORT_OPTIONS.map(o => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
              {viewMode === 'list' ? (
                <div className="space-y-2" data-testid="events-list-view">
                  {sortedEvents.map(event => {
                    const meta = TYPE_META[event.event_type] || TYPE_META.casual;
                    const rel = relativeEventLabel(event.event_date);
                    return (
                      <Link
                        key={event.event_id}
                        to={`/events/${event.event_id}`}
                        className="block"
                        data-testid={`event-row-${event.event_id}`}
                      >
                        <Card className="border-slate-100 hover:shadow-md transition-shadow">
                          <CardContent className="p-3 sm:p-4">
                            <div className="flex items-stretch gap-3">
                              <div
                                className="w-1.5 rounded-full flex-shrink-0"
                                style={{ backgroundColor: meta.color }}
                                aria-hidden="true"
                              />
                              <div className="flex-1 min-w-0">
                                <div className="flex items-start justify-between gap-2 flex-wrap">
                                  <h3
                                    className="font-semibold text-sm sm:text-base text-slate-900 truncate"
                                    style={{ fontFamily: 'Outfit, sans-serif' }}
                                  >
                                    {event.title}
                                  </h3>
                                  <div className="flex items-center gap-1 flex-wrap">
                                    <Badge className="rounded-full text-[10px] text-white" style={{ backgroundColor: meta.color }}>{meta.label}</Badge>
                                    {rel && (
                                      <Badge
                                        className={`rounded-full text-[10px] ${
                                          rel === 'Today' ? 'bg-[#df5681] text-white' :
                                          rel === 'Tomorrow' ? 'bg-[#56dfb4] text-slate-900' :
                                          'bg-slate-100 text-slate-700'
                                        }`}
                                      >
                                        {rel}
                                      </Badge>
                                    )}
                                    {event.is_free ? (
                                      <Badge className="rounded-full bg-[#56dfb4]/15 text-[#0f172a] text-[10px]">FREE</Badge>
                                    ) : (
                                      <Badge variant="outline" className="rounded-full text-[10px]">{formatEventCost(event)}</Badge>
                                    )}
                                    {event.is_charity && (
                                      <Badge className="rounded-full bg-[#df5681]/15 text-[#df5681] border border-[#df5681]/40 text-[10px] gap-0.5">
                                        <Heart className="w-2.5 h-2.5" /> Charity
                                      </Badge>
                                    )}
                                    {event.is_destination_event && (
                                      <Badge className="rounded-full bg-[#56c6df]/15 text-[#56c6df] border border-[#56c6df]/40 text-[10px]">
                                        🌍 Destination
                                      </Badge>
                                    )}
                                    {event.host_is_premium && (
                                      <PremiumInstructorBadge size="sm" />
                                    )}
                                  </div>
                                </div>
                                <div className="flex items-center gap-x-3 gap-y-1 text-xs text-slate-500 mt-1 flex-wrap">
                                  <span className="inline-flex items-center gap-1">
                                    <Calendar className="w-3 h-3" />
                                    {event.event_date ? formatEventDate(event.event_date, { weekday: 'short', month: 'short', day: 'numeric' }) : 'TBD'}
                                    {event.start_time && ` · ${formatEventTime(event.start_time, '', event.timezone)}`}
                                  </span>
                                  <span className="inline-flex items-center gap-1 min-w-0">
                                    <MapPin className="w-3 h-3 flex-shrink-0" />
                                    <span className="truncate">
                                      {event.is_destination_event && event.destination_label
                                        ? event.destination_label
                                        : [event.venue_name, event.city, event.state].filter(Boolean).join(', ')}
                                    </span>
                                  </span>
                                  {event.is_destination_event ? (
                                    <span className="text-[#56c6df]">✈️ Nationwide</span>
                                  ) : (event.distance !== null && event.distance !== undefined && (
                                    <span className="text-[#56c6df]">~{event.distance} mi</span>
                                  ))}
                                </div>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      </Link>
                    );
                  })}
                </div>
              ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="events-grid-view">
                {sortedEvents.map(event => {
                  const meta = TYPE_META[event.event_type] || TYPE_META.casual;
                  return (
                    <Card key={event.event_id} className="border-slate-100 hover:shadow-md hover:-translate-y-1 transition-all duration-300 overflow-hidden" data-testid={`event-card-${event.event_id}`}>
                      {event.banner_image ? (
                        <div className="relative h-32 overflow-hidden bg-slate-100">
                          <img
                            src={thumbUrl(event.banner_image)}
                            alt=""
                            loading="lazy"
                            decoding="async"
                            className="absolute inset-0 w-full h-full object-cover"
                          />
                          <div className="absolute top-0 left-0 right-0 h-3" style={{ backgroundColor: meta.color }} />
                        </div>
                      ) : (
                        <div className="h-3" style={{ backgroundColor: meta.color }} />
                      )}
                      <CardContent className="p-5">
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <Badge className="rounded-full text-xs text-white" style={{ backgroundColor: meta.color }}>{meta.label}</Badge>
                          <div className="flex items-center gap-1">
                            {(() => {
                              const rel = relativeEventLabel(event.event_date);
                              if (!rel) return null;
                              // "Today" pops in pink (urgent), "Tomorrow"
                              // in green (soon), everything else in subtle
                              // slate. Helps hosts notice imminent games
                              // without being shouty about everything.
                              const tone =
                                rel === 'Today' ? 'bg-[#df5681] text-white' :
                                rel === 'Tomorrow' ? 'bg-[#56dfb4] text-slate-900' :
                                'bg-slate-100 text-slate-700';
                              return (
                                <Badge className={`rounded-full text-xs ${tone}`} data-testid={`event-relative-${event.event_id}`}>{rel}</Badge>
                              );
                            })()}
                            {event.is_free ? (
                              <Badge className="rounded-full bg-[#56dfb4]/15 text-[#0f172a] text-xs">FREE</Badge>
                            ) : (
                              <Badge variant="outline" className="rounded-full text-xs">{formatEventCost(event)}</Badge>
                            )}
                            {event.is_charity && (
                              <Badge className="rounded-full bg-[#df5681]/15 text-[#df5681] border border-[#df5681]/40 text-xs gap-1" data-testid={`event-card-charity-badge-${event.event_id}`}>
                                <Heart className="w-3 h-3" /> Charity
                              </Badge>
                            )}
                            {event.is_destination_event && (
                              <Badge className="rounded-full bg-[#56c6df]/15 text-[#56c6df] border border-[#56c6df]/40 text-xs gap-1" data-testid={`event-card-destination-badge-${event.event_id}`}>
                                🌍 Destination
                              </Badge>
                            )}
                            {event.host_is_premium && (
                              <PremiumInstructorBadge
                                size="sm"
                                data-testid={`event-card-premium-instructor-badge-${event.event_id}`}
                              />
                            )}
                          </div>
                        </div>
                        <h3 className="font-semibold text-base mb-2 line-clamp-2" style={{ fontFamily: 'Outfit, sans-serif' }}>{event.title}</h3>
                        <div className="space-y-1.5 text-sm text-slate-500 mb-3">
                          <div className="flex items-center gap-1.5">
                            <Calendar className="w-3.5 h-3.5" />
                            {event.event_date ? formatEventDate(event.event_date, { weekday: 'short', month: 'short', day: 'numeric' }) : 'TBD'}
                            {event.start_time && ` at ${formatEventTime(event.start_time, '', event.timezone)}`}
                          </div>
                          <div className="flex items-center gap-1.5">
                            <MapPin className="w-3.5 h-3.5" />
                            {event.is_destination_event && event.destination_label
                              ? event.destination_label
                              : <>{event.venue_name}{event.city && `, ${event.city}`}{event.state && `, ${event.state}`}</>}
                          </div>
                          {event.is_destination_event ? (
                            <div className="flex items-center gap-1.5 text-xs text-[#56c6df]">
                              ✈️ Open to players nationwide
                            </div>
                          ) : (event.distance !== null && event.distance !== undefined && (
                            <div className="flex items-center gap-1.5 text-xs text-[#56c6df]">
                              ~{event.distance} miles away
                            </div>
                          ))}
                        </div>
                        <div className="flex flex-wrap gap-1 mb-3">
                          {(event.skill_levels || []).slice(0, 3).map(s => (
                            <span key={s} className="px-2 py-0.5 rounded-full bg-slate-100 text-xs capitalize">{s}</span>
                          ))}
                        </div>
                        <Link to={`/events/${event.event_id}`}>
                          <Button size="sm" className="w-full rounded-full bg-[#56dfb4] text-slate-900 hover:bg-[#3ec49b]" data-testid={`view-event-${event.event_id}`}>
                            View Details
                          </Button>
                        </Link>
                      </CardContent>
                    </Card>
                  );
                })}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
